import {PaymentMethodAlreadyDestroyedError, type ApplePay, type Payments} from '@square/web-payments-sdk-types';
import {$qs} from '../../../../@shared/ts/dom';
import {render} from '../../reducers/rootReducer';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {handleTokenResult} from './square';
import {type SquareGateway, type ProcessedTokenResult} from '../../models/square';
import applepayBadgeURL from '../../../img/badge/apple-pay.svg';
import applepayImageURL from '../../../img/marks/apple-pay.svg';
import applepayButtonURL from '../../../img/apple-pay-button.png';
import {GatewayEligibility, type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {Feature} from '../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../@type/features';
import {captureSentryException} from '../../../../@shared/ts/sentry';
import {type PaymentRequest} from '@square/web-payments-sdk-types';
import {debounce} from '../../util/debounce';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';

// https://developer.squareup.com/reference/sdks/web/payments/digital-wallets/apple-pay
function initSquareApplePayMethod(): SquareGateway {
	let apay: ApplePay | null = null;
	let buttonInstanceWaitingInterval: NodeJS.Timer | undefined;

	const $applePayButton = getButton();

	const config: GatewayConfiguration = {
		name: Feature.metadata<string>(FeatureFlag.SQUARE_APPLEPAY_GATEWAY, 'title') ?? getLocaleText('Apple Pay'),
		gatewayId: 'peachpay_square_applepay',
		description: getDescription(),
		assets: {
			badge: {src: applepayBadgeURL},
			title: {src: applepayImageURL},
		},
		hasCustomButton: true,
	};

	const debouncedActivate = debounce((async (payments: Payments, paymentRequest: PaymentRequest) => {
		if (apay) {
			return;
		}

		if (PaymentConfiguration.eligibleGateway(config.gatewayId) !== GatewayEligibility.Eligible) {
			config.initialized = false;
			store.dispatch(render());
			return;
		}

		try {
			apay = await payments.applePay(paymentRequest);

			config.initialized = true;
			store.dispatch(render());
		} catch (error: unknown) {
			config.initialized = false;
			store.dispatch(render());

			console.error(error);
			captureSentryException(error as Error, {
				paymentRequest,
			});
		}
	}) satisfies SquareGateway['activate']);

	return {
		config,
		activate: debouncedActivate,
		reset() {
			clearInterval(buttonInstanceWaitingInterval);

			const oldInstance = apay;
			apay = null;

			oldInstance?.destroy()?.catch(error => {
				if (error instanceof PaymentMethodAlreadyDestroyedError) {
					return;
				}

				console.error(error);
				captureSentryException(error as Error);
			});
		},
		attach(selector: string) {
			clearInterval(buttonInstanceWaitingInterval);
			buttonInstanceWaitingInterval = setInterval(() => {
				if (apay) {
					clearInterval(buttonInstanceWaitingInterval);

					$qs(selector, $el => $el.insertAdjacentElement('afterbegin', $applePayButton));
				}
			}, 200);
		},
		detach() {
			$applePayButton.remove();
		},
		async tokenize(): Promise<ProcessedTokenResult> {
			try {
				if (!apay) {
					return [null, null, {
						paymentStatus: 'creationfailed',
						note: 'Failed to find Apple Pay instance while attempting to tokenize the payment.',
					}];
				}

				const result = await apay.tokenize();
				return handleTokenResult(result);
			} catch (error: unknown) {
				const update: ITransactionUpdate = {
					paymentStatus: 'creationfailed',
					note: 'An unknown exception occured while tokenizing the payment.',
				};

				if (error instanceof Error) {
					update.note = error.message;
				}

				return [null, null, update];
			}
		},
	};
}

function getDescription(): string {
	return /* html */ `
<img style="display: block; text-align: left; height: 1.5rem; " src="${applepayImageURL}">
<p style="text-align: left; margin: 0.5rem 0;">${getLocaleText('Apple Pay selected for checkout.')}</p>
<hr/>
<p style="text-align: left; margin: 0.5rem 0 0;" class="muted">${getLocaleText('Another step will appear after submitting your order to complete your purchase details.')}<p>`;
}

function getButton(): HTMLButtonElement {
	const $temp = document.createElement('div');

	$temp.innerHTML = /* html */ `<button class="square-apay-btn">
        <style>
            .square-apay-btn{
                background-image: url(${applepayButtonURL});
                background-color: black;
                border: none; height: 55px;
                width: 15rem;
                background-size: contain;
                border-radius: 0.2rem;
                background-position: center;
            }

            .square-apay-btn:hover{
                opacity: 0.8;
            }
        </style>
    </button>`;

	return $temp.querySelector('button')!;
}

export {initSquareApplePayMethod};
